// Final Analytical Review Card V2 · design 5 — monthly trends from the general ledger. Heavy work: the result is computed in the
// background (job kind far_monthly_trends) and stored; this part shows the stored result with the shared Run / Rescan banner and
// follows a running job. Flagged months and the year-end spike link to the cut-off worksheets and to journal entry testing.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import StoredResultBanner from "@/components/StoredResultBanner";
import { asArray } from "@/lib/asArray";
import { fmtMonth } from "@/lib/dt";
import { fmt, fmtPct, L } from "@/components/far/FarParts";
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const RUNNING = new Set(["queued", "running", "pending"]);

// the default links when the stored result names none (codes of the existing cut-off worksheets)
const DEFAULT_LINKS = [
  { code: "4101-06", path: "/head-folders?open=4101-06", label_en: "Revenue cut-off", label_ar: "الفصل الزمني للإيرادات" },
  { code: "5101-06", path: "/head-folders?open=5101-06", label_en: "Cost of sales cut-off", label_ar: "الفصل الزمني لتكلفة المبيعات" },
  { code: "3203-07", path: "/head-folders?open=3203-07", label_en: "Search for unrecorded liabilities", label_ar: "البحث عن الالتزامات غير المسجلة" },
];

// Step 11d · add-on: month, revenue, cost of sales, gross margin, payroll, other costs
const MONTH_COLS = [130, 140, 140, 120, 140, 140];
const MONTH_MINS = [96, 100, 100, 90, 100, 100];

export default function FarMonthly({ engId, t, lang, locked, pollMs = 2000 }) {
  const cols = useResizableColumns({ storageKey: "far-monthly", defaults: MONTH_COLS, mins: MONTH_MINS });
  const [st, setSt] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/far/${engId}/monthly`, { _silent: true });
      setSt(r.data || {});
      return r.data || {};
    } catch (e) { setSt({ status: "failed", error: toErrMsg(e, tRef.current("far.loadFailed")) }); return null; }
  }, [engId]);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (st && RUNNING.has(st.status)) timer.current = setTimeout(load, pollMs);
  }, [st, load, pollMs]);

  const run = async () => {
    setStarting(true);
    try {
      await api.post(`/far/${engId}/monthly/refresh`);
      setSt((s) => ({ ...(s || {}), status: "running" }));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("far.loadFailed"))); }
    finally { setStarting(false); }
  };

  const res = st?.result || null;
  const running = !!st && RUNNING.has(st.status);
  const state = !res ? "not_run" : st?.stale ? "stale" : "fresh";
  const months = asArray(res?.months);
  const flagged = asArray(res?.flagged);
  const spikes = asArray(res?.year_end_spike);
  const links = asArray(res?.links).length ? asArray(res?.links) : DEFAULT_LINKS;
  const flagSet = new Set(flagged.map((f) => `${f.month}|${f.metric}`));
  const cell = (m, metric, v, pct) => (
    <td className={`px-2 py-1 text-end text-xs tabular-nums ${flagSet.has(`${m.month}|${metric}`) ? "bg-amber-100 font-semibold dark:bg-amber-900/40" : ""}`}
        data-testid={`far-month-${m.month}-${metric}`}>{pct ? fmtPct(v) : fmt(v)}</td>
  );
  return (
    <section className="rounded-md border" data-testid="far-monthly">
      <h3 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold dark:bg-slate-900/50">{t("far.sec.monthly")}</h3>
      <div className="space-y-2 p-3">
        <StoredResultBanner lang={lang} state={state} runAt={st?.computed_at || ""} runBy={st?.computed_by || ""} running={running || starting}
                            progress={st?.progress || 0} error={st?.status === "failed" ? jobErrorText(st, lang, t) : ""} onRun={run}
                            canRun={!locked} testid="far-monthly-banner" runTestId="far-monthly-run" />
        {!res && !running && <div className="text-xs text-muted-foreground">{t("far.monthlyNeedsRun")}</div>}
        {res && res.available === false && (
          <div className="text-xs text-muted-foreground" data-testid="far-monthly-unavailable">
            {(lang === "ar" ? (res.reason_ar || res.reason_en) : res.reason_en) || t("far.monthlyNoData")}
          </div>
        )}
        {res && res.available !== false && (
          <>
            <div className="overflow-x-auto">
              <ResizableTable cols={cols} className="w-full border-collapse" data-testid="far-monthly-table">
                <thead className="border-b">
                  <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <ResizableTh index={0} className="px-2 py-1 text-start">{t("far.col.month")}</ResizableTh>
                    <ResizableTh index={1} className="px-2 py-1 text-end">{t("far.col.revenue")}</ResizableTh>
                    <ResizableTh index={2} className="px-2 py-1 text-end">{t("far.col.costOfSales")}</ResizableTh>
                    <ResizableTh index={3} className="px-2 py-1 text-end">{t("far.col.grossMargin")}</ResizableTh>
                    <ResizableTh index={4} className="px-2 py-1 text-end">{t("far.col.payroll")}</ResizableTh>
                    <ResizableTh index={5} className="px-2 py-1 text-end">{t("far.col.otherCosts")}</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month} className="border-b">
                      <td className="px-2 py-1 text-xs">{fmtMonth(m.month)}</td>
                      {cell(m, "revenue", m.revenue)}
                      {cell(m, "cost_of_sales", m.cost_of_sales)}
                      {cell(m, "gross_margin_pct", m.gross_margin_pct, true)}
                      {cell(m, "payroll", m.payroll)}
                      {cell(m, "other_operating_costs", m.other_operating_costs)}
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            </div>
            <div className="grid gap-3 text-xs md:grid-cols-2">
              <div data-testid="far-monthly-flagged">
                <div className="font-semibold">{t("far.monthlyFlagged")}</div>
                {flagged.length === 0 ? <div className="text-muted-foreground">{t("far.monthlyNone")}</div> : (
                  <ul className="list-disc ps-4">
                    {flagged.map((f, i) => (
                      <li key={i}>{fmtMonth(f.month)} · {L(f, "label", lang) || f.metric}: {fmt(f.value)}
                        {(f.reason_en || f.reason_ar) ? ` — ${lang === "ar" ? (f.reason_ar || f.reason_en) : f.reason_en}` : ""}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {(lang === "ar" ? res.rules?.band_ar : res.rules?.band_en) || t("far.monthlyBand")}
                </div>
              </div>
              <div data-testid="far-monthly-spike">
                <div className="font-semibold">{t("far.monthlySpike")}</div>
                {spikes.length === 0 ? <div className="text-muted-foreground">{t("far.monthlyNoSpike")}</div> : (
                  <ul className="list-disc ps-4">
                    {spikes.map((s, i) => (
                      <li key={i}>{fmtMonth(s.month)} · {L(s, "label", lang) || s.metric}: {fmt(s.value)} ({fmt(s.avg_other)})</li>
                    ))}
                  </ul>
                )}
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {(lang === "ar" ? res.rules?.spike_ar : res.rules?.spike_en) || t("far.monthlySpikeRule")}
                </div>
              </div>
            </div>
            {(flagged.length > 0 || spikes.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="far-monthly-links">
                <span className="font-semibold">{t("far.cutoffLinks")}:</span>
                {links.filter((l) => l.path !== "/fieldwork/a9001").map((l) => (   /* JE testing: the one link below */
                  <Link key={l.path} to={l.path} className="underline" data-testid={`far-monthly-link-${l.code || l.path}`}>
                    {L(l, "label", lang) || l.label_en}
                  </Link>
                ))}
                <Link to="/fieldwork/a9001" className="underline" data-testid="far-monthly-link-je">{t("far.jeLink")}</Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
